import { Component, inject, OnInit } from '@angular/core';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AddExamInfo } from '../../shared/interfaces/reserve-add.model';
import {
  BusinessExamInfo,
  BusinessPackageInfo,
  ExamInfo,
  Group,
  GroupInfo,
} from '../../shared/interfaces/reserve-loading.model';
import { MatButton, MatIconButton } from '@angular/material/button';
import { Router } from '@angular/router';
import { EligibilityQuestionnaireFieldRequest, ExamItemName, Question } from '../../shared/interfaces/reserve-confirm-survey.model';
import { NumberCommaPipe } from '../../pipes/number-comma.pipe';
import { NgOptimizedImage } from '@angular/common';
import { ID_LOGIN_TYPE, LOGIN_REGISTER_TABS } from '../../shared/constants/common.constants';
import { MatDialog } from '@angular/material/dialog';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { ReserveConfirmSurveyService } from '../../shared/services/reserve-confirm-survey.service';
import Decimal from 'decimal.js';
import { CheckReminderRequest, Reminder } from '../../shared/interfaces/reserve-confirm-reminder.model';
import { ReserveConfirmReminderService } from '../../shared/services/reserve-confirm-reminder.service';
import { AuthService } from '../../shared/services/auth.service';

/**
 * 確認健檢方案組件
 */
@Component({
  selector: 'app-reserve-confirm',
  standalone: true,
  imports: [
    TranslatePipe,
    MatButton,
    NumberCommaPipe,
    NgOptimizedImage,
    MatIconButton,
  ],
  templateUrl: './reserve-confirm.component.html',
  styleUrl: './reserve-confirm.component.scss',
})
export class ReserveConfirmComponent implements OnInit {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  router: Router = inject(Router);
  dialog: MatDialog = inject(MatDialog);
  translateService: TranslateService = inject(TranslateService);
  reserveConfirmSurveyService: ReserveConfirmSurveyService = inject(ReserveConfirmSurveyService);
  reserveConfirmReminderService: ReserveConfirmReminderService = inject(ReserveConfirmReminderService);
  authService: AuthService = inject(AuthService);

  protected readonly Math = Math;

  // 檢查提醒清單
  reminderList: Array<Reminder> = [];

  // 是否勾選同意條款
  agreeChecked: boolean[] = [];

  // 選擇之方案
  selectedPackage: string = '';
  // 選擇之方案金額
  selectedPackagePrice: number = 0;
  // 方案檢項
  examInfoList: Array<ExamInfo> = [];
  // 加購項目
  addExamInfoList: Array<AddExamInfo> = [];

  // 個人方案底下的所有檢項(按照examType分類)
  examInfoListByType: Map<string, Map<string, Array<ExamInfo>>> = new Map();

  // 企業方案底下的examInfo(按照deptName分類)
  businessGroupUnderPackageByDept: Map<string, Array<Group>> = new Map();

  // 企業方案底下的group(按照deptName分類)
  businessExamInfoUnderPackageByDept: Map<string, Array<BusinessExamInfo>> =
    new Map();

  // 企業補助金額
  businessSubsidyAmount: number = 0;

  // 無法施作的檢項及其未通過的問題 { examItem: string, question: Array<Question> }
  invalidExamItemQuestionMap: Map<string, Array<Question>> | undefined;

  // 用戶主動移除的方案檢項
  removeItemQuestionMap: Array<ExamInfo> = [];

  // 檢查項目map { examItem: string, ExamInfo | AddExamInfo | BusinessExamInfo }
  examItemMap: Map<string, ExamInfo | AddExamInfo | BusinessExamInfo> =
    new Map();

  // 組合檢項map { groupItem: string, Group }
  groupMap: Map<string, Group> = new Map();

  // deptName是否展開 { deptName: isOpen }
  isDeptNameOpen: Map<string, boolean> = new Map();

  // examType底下deptName是否展開 { examType: { deptName: isOpen } }
  isDeptNameOpenByExamType: Map<string, Map<string, boolean>> = new Map();

  // 扣掉補助金額前的總金額
  beforeSponsorAmount: number = 0;

  // 原套檢金額
  originalPackagePrice: number = 0;

  // allCheckupDiscountRate = '0.75367';
  // aiExclusiveDiscountRate = '0.71014'; 
  // basicCheckupDiscountRate = '0.59921';

  // 活動折扣率
  partyDiscountRate: string = '';

  ngOnInit(): void {
    // 將當前預約步驟設置為5
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 5);
    });

    const examItemList = this.sharedStorageService.get('examItemList');
    console.log("examItemList原價:", examItemList);

    this.removeItemQuestionMap = this.sharedStorageService.get('removeItemQuestionMap') || [];

    const partyDiscountRate = this.sharedStorageService.get('partyDiscountRate') ?? '';
    if (partyDiscountRate) {
      this.partyDiscountRate = partyDiscountRate;
    }

    // 初始化無法施作的檢項 - 處理普通物件轉 Map
    const invalidExamData = this.sharedStorageService.get('invalidExamQuestionMap');
    if (invalidExamData) {
      this.invalidExamItemQuestionMap = new Map<string, Array<Question>>();
      
      // 將普通物件轉換為 Map
      Object.entries(invalidExamData).forEach(([key, value]) => {
        this.invalidExamItemQuestionMap!.set(key, value);
      });

      // 去重處理
      const newMap = new Map<string, Question[]>();
      this.invalidExamItemQuestionMap.forEach((questions, examItem) => {
        const uniqueQuestionsMap = new Map<string, Question>();
        questions.forEach(q => {
          if (!uniqueQuestionsMap.has(q.questionId)) {
            uniqueQuestionsMap.set(q.questionId, q);
          }
        });
        newMap.set(examItem, Array.from(uniqueQuestionsMap.values()));
      });
      this.invalidExamItemQuestionMap = newMap;
       console.log('去重後的 invalidExamItemQuestionMap:', this.invalidExamItemQuestionMap);
    }

    if (
      this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.PERSONAL
    ) {
      // 個人預約
      // 取得選擇方案
      this.selectedPackage =
        this.sharedStorageService.get('selectedPackage') ?? '';
      // 取得選擇方案金額
      this.selectedPackagePrice =
        Number(this.sharedStorageService.get('paymentAmount')) ?? 0;

      // 取得方案檢項
      this.examInfoList = this.sharedStorageService.get('examItemList') ?? [];
      console.log("examInfoList:", this.examInfoList);
      this.examInfoListByType = this.examInfoList
        .filter(examInfo => {
          // 過濾掉被移除的檢項
          return examInfo.selectFlag === 1 && !this.removeItemQuestionMap.some(removedItem => 
            removedItem.examItem === examInfo.examItem
          );
        })
        .reduce((typeMap, examInfo) => {
          const type = examInfo.examType ?? 'unknown';
          const dept = examInfo.deptName ?? 'unknown';
          if (!typeMap.has(type)) {
            typeMap.set(type, new Map<string, ExamInfo[]>());
          }
          const deptMap = typeMap.get(type)!;
          if (!deptMap.has(dept)) {
            deptMap.set(dept, []);
          }
          deptMap.get(dept)!.push(examInfo);
          return typeMap;
      }, new Map<string, Map<string, ExamInfo[]>>());

      console.log("removeItemQuestionMap", this.removeItemQuestionMap);
      console.log("examInfoListByType:", this.examInfoListByType);

      // 初始化檢查項目map
      this.sharedStorageService
        .get('examItemList')
        ?.forEach((examItem: ExamInfo) => {
          this.examItemMap.set(examItem.examItem, examItem);
        });

      // 初始化dept是否展開
      for (let deptMap of this.examInfoListByType.values()) {
        for (let deptKey of deptMap.keys()) {
          this.isDeptNameOpen.set(deptKey, false);
          this.isDeptNameOpenByExamType.set(deptKey, new Map<string, boolean>());
        }
      }
    } else {
      console.log("企業預約嗎?");
      // 企業預約
      const businessPlanResponse = this.sharedStorageService.get(
        'businessPlanResponse'
      );
      const businessSelectedPackage = this.sharedStorageService.get(
        'businessSelectedPackage'
      );
      if (businessPlanResponse || businessSelectedPackage) {
        // 取得選擇方案
        this.selectedPackage = businessSelectedPackage?.packageName ?? '';
        // 取得選擇方案金額
        this.selectedPackagePrice = Number(businessSelectedPackage?.price) ?? 0;

        const modifyType = this.sharedStorageService.get('modifyType');
        // 取得補助金額
        if (modifyType === 'modifyExam') {
          this.businessSubsidyAmount = Number(this.sharedStorageService.get('sponsorAmt'));
          // 取得原始套檢金額
          this.originalPackagePrice = this.sharedStorageService.get('originalPackagePrice') ?? 0;
        } else {
          this.businessSubsidyAmount = Number(
          businessPlanResponse?.companyInfo.sponsorAmt);
        }

        // 取得方案檢項
        const examItemList = this.sharedStorageService.get('examItemList') ?? [];
        
        // 統一 deptName 格式的函數
        const normalizeDeptName = (deptName: string): string => {
          // 移除英文部分，只保留中文
          return deptName.replace(/\([^)]*\)/g, '').trim();
        };

        // 初始化 examInfoListByType
        this.examInfoListByType = examItemList
          .filter(examInfo => {
            // 過濾掉被移除的檢項
            return !this.removeItemQuestionMap.some(removedItem => 
              removedItem.examItem === examInfo.examItem
            );
          })
          .reduce((typeMap, examInfo) => {
            const type = examInfo.examType ?? 'unknown';
            const dept = normalizeDeptName(examInfo.deptName ?? 'unknown');
            if (!typeMap.has(type)) {
              typeMap.set(type, new Map<string, ExamInfo[]>());
            }
            const deptMap = typeMap.get(type)!;
            if (!deptMap.has(dept)) {
              deptMap.set(dept, []);
            }
            deptMap.get(dept)!.push(examInfo);
            return typeMap;
          }, new Map<string, Map<string, ExamInfo[]>>());

        // 初始化檢查項目map
        examItemList.forEach((examItem: ExamInfo) => {
          this.examItemMap.set(examItem.examItem, examItem);
        });

        // 初始化dept是否展開
        for (let deptMap of this.examInfoListByType.values()) {
          for (let deptKey of deptMap.keys()) {
            this.isDeptNameOpen.set(deptKey, false);
            this.isDeptNameOpenByExamType.set(deptKey, new Map<string, boolean>());
          }
        }

        console.log("企業預約 examInfoListByType:", this.examInfoListByType);
      }
    }

    // 若有無法施作的檢項，則僅展開有該項目的部門
    if (this.invalidExamItemQuestionMap) {
      // 個人預約
      for (const [type, deptMap] of this.examInfoListByType.entries()) {
        for (const [deptKey, examList] of deptMap.entries()) {
          const hasInvalid = examList.some(examInfo =>
            this.invalidExamItemQuestionMap!.has(examInfo.examItem)
          );
          if (hasInvalid) {
            if (!this.isDeptNameOpenByExamType.has(type)) {
              this.isDeptNameOpenByExamType.set(type, new Map<string, boolean>());
            }
            this.isDeptNameOpenByExamType.get(type)!.set(deptKey, true);
            this.isDeptNameOpen.set(deptKey, true);
          }
        }
      }
    }

    // 若有被移除的檢項，則展開有該項目的部門
    // if (this.removeItemQuestionMap) {
    //   // 個人預約
    //   this.removeItemQuestionMap.forEach(removedItem => {
    //     for (const [type, deptMap] of this.examInfoListByType.entries()) {
    //       for (const [deptKey, examList] of deptMap.entries()) {
    //         const hasRemoved = examList.some(
    //           examInfo => examInfo.examItem === removedItem.examItem
    //         );
    //         if (hasRemoved) {
    //           this.isDeptNameOpen.set(deptKey, true);
    //         }
    //       }
    //     }
    //   });
    // } 


    // 企業預約
    for (let [
      deptName,
      examInfoList,
    ] of this.businessExamInfoUnderPackageByDept.entries()) {
      const hasInvalidExam = examInfoList.some(examInfo =>
        this.invalidExamItemQuestionMap!.has(examInfo.examItem)
      );
      if (hasInvalidExam) {
        this.isDeptNameOpen.set(deptName, true);
      }
    }
    

  // 取得加購項目
  this.addExamInfoList =
    this.sharedStorageService.get('addExamItemList') ?? [];

  // 初始化加選項目map
  this.sharedStorageService
    .get('addExamItemList')
    ?.forEach((examItem: AddExamInfo) => {
      this.examItemMap.set(examItem.examItem, examItem);
    });
  }

  // 個人檢查是否有無法施作的檢項只顯示一次圖示
  shouldShowWarning(examList: ExamInfo[]): boolean {
    return examList.some(info =>
      this.invalidExamItemQuestionMap?.has(info.examItem)
    );
  }

  // 企業檢查是否有無法施作的檢項只顯示一次圖示
  shouldShowPackageWarning(deptName: string): boolean {
    const list = this.businessExamInfoUnderPackageByDept.get(deptName);
    return (
      Array.isArray(list) &&
      list.some(info => this.invalidExamItemQuestionMap?.has(info.examItem))
    );
  }

  // 檢查項目是否被使用者主動移除
  isItemRemoved(examItem: string): boolean {
    return this.removeItemQuestionMap.some(item => item.examItem === examItem);
  }

  // 取得被移除項目的名稱
  getRemovedItemName(examItem: string): string {
    return this.removeItemQuestionMap.find(item => item.examItem === examItem)
      ?.examName || '';
  }

  // 加選項目總金額
  get addExamInfoPrice(): number {
    return this.addExamInfoList.reduce(
      (total, item) => total + (Number(item.price) || 0),
      0
    );
  }

  // 移除項目金額
  get removeExamInfoPrice(): number {
    if (!this.invalidExamItemQuestionMap || this.invalidExamItemQuestionMap.size === 0) {
      return 0;
    }

    let totalRemovePrice = 0;
    const addExamItemList = this.sharedStorageService.get('addExamItemList') ?? [];
    const examItemList = this.sharedStorageService.get('examItemList') ?? [];

    this.invalidExamItemQuestionMap.forEach((questions, examItem) => {
      // 檢查是否為加選項目
      const addExamInfo = addExamItemList.find((item: AddExamInfo) => item.examItem === examItem);
      
      if (addExamInfo) {
        // 加選項目:直接加上價格
        totalRemovePrice += Number(addExamInfo.price) || 0;
      } else {
        // 方案內檢項:需要計算折扣後的價格差異
        // (保留原有的複雜計算邏輯)
      }
    });

    // 如果都是加選項目,直接返回
    const allAreAddItems = Array.from(this.invalidExamItemQuestionMap.keys())
      .every(examItem => addExamItemList.some((item: AddExamInfo) => item.examItem === examItem));
    
    if (allAreAddItems) {
      return totalRemovePrice;
    }

    // 取得原始方案金額
    const originalPackagePrice = this.packageExamPrice;

    // 計算移除無法施作檢項後的原始總價
    const remainingOriginalPrice = examItemList
      .filter((examInfo: ExamInfo) => 
        !this.invalidExamItemQuestionMap!.has(examInfo.examItem) &&
        (examInfo.selectFlag ?? 1) === 1
      )
      .reduce((total: Decimal, examInfo: ExamInfo) => {
        return total.add(new Decimal(examInfo.examPrice ?? 0));
      }, new Decimal(0));

    // 用移除後的原始總價計算新的動態折扣率
    const newDiscountRate = this.movedDiscountRate(remainingOriginalPrice);
    
    // 計算移除後的折扣總金額
    const remainingDiscountedPrice = examItemList
      .filter((examInfo: ExamInfo) => 
        !this.invalidExamItemQuestionMap!.has(examInfo.examItem) &&
        (examInfo.selectFlag ?? 1) === 1
      )
      .reduce((total: number, examInfo: ExamInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice ?? 0);
        
        if (examInfo.rateStatus === true) {
          let discountedPrice = originalPrice.mul(newDiscountRate);
          let roundedPrice = discountedPrice.div(10).ceil().mul(10);
          
          if (this.partyDiscountRate) {
            roundedPrice = roundedPrice.mul(new Decimal(this.partyDiscountRate));
            roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
          }
          return total + roundedPrice.toNumber();
        } else {
          return total + originalPrice.toNumber();
        }
      }, 0);

    // 方案內檢項移除金額 + 加選項目移除金額
    return (originalPackagePrice - remainingDiscountedPrice) + totalRemovePrice;
  }

  // 判斷檢項是否為方案內檢項
  private isExamItemUnderPackage(examItem: string): boolean {
    if (this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.PERSONAL) {
      // 個人預約：檢查是否在 examItemList 中
      const examItemList = this.sharedStorageService.get('examItemList') ?? [];
      return examItemList.some((item: ExamInfo) => item.examItem === examItem);
    } else {
      // 企業預約：檢查是否在企業方案中
      return this.businessExamInfoUnderPackageByDept.size > 0 && 
             Array.from(this.businessExamInfoUnderPackageByDept.values())
             .some(examInfoList => examInfoList.some(examInfo => examInfo.examItem === examItem));
    }
  }

  // 取得無法施作檢項名稱
  getExamName(examItem: string): string {
    // 先檢查 examItemMap (方案內檢項)
    if (this.examItemMap.has(examItem)) {
      const examInfo = this.examItemMap.get(examItem);
      if (examInfo) {
        return examInfo.examName || '';
      }
    } 
    
    // 檢查加選項目
    const addExamItemList = this.sharedStorageService.get('addExamItemList') ?? [];
    const addExamInfo = addExamItemList.find((item: AddExamInfo) => item.examItem === examItem);
    if (addExamInfo) {
      return addExamInfo.examName || '';
    }
    
    // 檢查 removeItemQuestionMap
    const removeItem = this.removeItemQuestionMap.find(item => item.examItem === examItem);
    if (removeItem) {
      return removeItem.examName || '';
    }
    
    // 檢查特殊麻醉項目
    if (examItem.includes('PE001005')) {
      return '(新)單項麻醉(anesthesia)';
    } else if (examItem.includes('PE001006')) {
      return '(新)雙項麻醉(anesthesia)';
    }
    
    // 檢查組合檢項
    const group = this.groupMap.get(examItem);
    if (group) {
      return group.groupName || '';
    }
    
    return '';
  }

  // 取得單一檢項價格
  getExamInfoPrice(examItem: string): number {
    if (this.examItemMap.has(examItem)) {
      // 單一檢項ExamInfo/AddExamInfo/BusinessExamInfo
      const examInfo = this.examItemMap.get(examItem);
      if (examInfo) {
        // TODO BusinessExamInfo金額
        if ('examPrice' in examInfo) {
          // ExamInfo
          return Number(examInfo.examPrice) || 0;
        } else if ('price' in examInfo) {
          // AddExamInfo
          return Number(examInfo.price) || 0;
        }
      }
    } else {
      // 組合檢項group
      const group = this.groupMap.get(examItem);
      if (group) {
        // TODO group檢項金額
      }
    }

    return 0;
  }

  // 企業方案deptName列表
  get businessDeptNames(): Array<string> {
    return (
      this.sharedStorageService
        .get('businessSelectedPackage')
        ?.deptList.map(
          businessPackageInfo => businessPackageInfo.deptName
        ) ?? []
    );
  }

  // 總金額
  get paymentAmount(): number {
    // 如果 sharedStorageService 中已經有鎖定的 paymentAmount,直接使用
    const lockedPaymentAmount = this.sharedStorageService.get('lockedPaymentAmount');
    if (lockedPaymentAmount !== null && lockedPaymentAmount !== undefined) {
      return Number(lockedPaymentAmount);
    }

    let paymentAmount =
      this.packageExamPrice +
      this.addExamInfoPrice -
      this.removeExamInfoPrice;
    // 扣掉補助金額前的總金額
    this.beforeSponsorAmount = paymentAmount;
    if (
      this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.ENTERPRISE
    ) {
      if (this.businessSubsidyAmount > 0 && this.businessSubsidyAmount) {
        paymentAmount -= this.businessSubsidyAmount; // 企業預約扣除補助金額
      }
    }
    console.log('重新計算的 paymentAmount:', paymentAmount);
    return paymentAmount;
  }

  // 點擊"調整項目"
  adjustItem() {
    // 企業預約判斷
    if (this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.ENTERPRISE) {
      this.invalidExamItemQuestionMap = undefined;
      this.sharedStorageService.remove('invalidExamQuestionMap');
      this.sharedStorageService.set('packagePrice', this.packageExamPrice.toString());
      this.sharedStorageService.set('paymentAmount', this.paymentAmount - this.removeExamInfoPrice);
      this.sharedStorageService.set('removeItemQuestionMap', this.removeItemQuestionMap);
      this.router.navigate(['/healthcheck/reserve/additem']).then();
    } else {
      this.invalidExamItemQuestionMap = undefined;
      this.sharedStorageService.remove('invalidExamQuestionMap');
      this.sharedStorageService.set('packagePrice', this.packageExamPrice.toString());
      this.sharedStorageService.set('paymentAmount', this.paymentAmount - this.removeExamInfoPrice);
      this.sharedStorageService.set('removeItemQuestionMap', this.removeItemQuestionMap);
      this.router.navigate(['/healthcheck/reserve/additem']).then();
    }
  }

  // 計算方案檢項總金額
  get packageExamPrice(): number {
    if (this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.PERSONAL) {
      // 個人預約：加總已選擇的檢項價格
      // return this.examInfoList.reduce((total, examInfo) => {
      //   return total + (Number(examInfo.examPrice) || 0);
      // }, 0);
      const packagePrice = this.sharedStorageService.get('originalPrice') ?? 0;
      return Number(packagePrice) || 0;
    } else {
      // 企業預約：加總企業方案檢項價格
      let total = 0;
      total = Number(this.sharedStorageService.get('packagePrice')) ?? 0;
      return total;
    }
  }

  // 顯示檢項dialog
  showExamDialog(examInfo: ExamInfo | AddExamInfo | BusinessExamInfo) {
    const examDialog = this.dialog.open(SingleActionDialogComponent, {
      data: {
        title: examInfo.examName,
        message: examInfo.examMemo,
        buttonText: this.translateService.instant('common.button.i-understood'),
        showCloseButton: false,
        action: () => examDialog.close(),
      },
    });
  }

  // 顯示組合檢項dialog
  showGroupDialog(group: Group) {
    const examDialog = this.dialog.open(SingleActionDialogComponent, {
      data: {
        title: group.groupName,
        message: group.groupMemo,
        buttonText: this.translateService.instant('common.button.i-understood'),
        showCloseButton: false,
        action: () => examDialog.close(),
      },
    });
  }

  // 驗證企業修改檢項時,原套檢金額是否有超過總金額
  get isPackagePriceValid(): boolean {
    const reserveType = this.sharedStorageService.get('reserveType');
    const modifyType = this.sharedStorageService.get('modifyType');
    const billAmt = Number(this.sharedStorageService.get('paymentAmount'));
    const sponsorAmt = Number(this.sharedStorageService.get('sponsorAmt')) || 0;
    
    // 企業預約 且 調整檢項時的驗證
    if (reserveType === ID_LOGIN_TYPE.ENTERPRISE && modifyType === 'modifyExam') {
      // 計算扣除補助金額前的總金額 (因為 billAmt 已經是扣除補助後的金額)
      const totalAmountBeforeSponsor = billAmt + sponsorAmt;
      console.log("totalAmountBeforeSponsor", totalAmountBeforeSponsor);
      
      // 當扣除補助前的總金額 < 套檢金額時,返回 false 觸發彈窗
      if (totalAmountBeforeSponsor < this.originalPackagePrice) {
        return false;
      }
    }
    
    return true;
  }

  // 點擊"確認檢查項目"
  confirm() {
    // 在最開始就鎖定金額並存入 lockedPaymentAmount
    const finalPaymentAmount = this.paymentAmount;
    this.sharedStorageService.set('lockedPaymentAmount', finalPaymentAmount);
    this.sharedStorageService.set('paymentAmount', finalPaymentAmount);
    
    console.log('確認時鎖定的 paymentAmount:', finalPaymentAmount);
    console.log('packageExamPrice:', this.packageExamPrice);
    console.log('addExamInfoPrice:', this.addExamInfoPrice);
    console.log('removeExamInfoPrice:', this.removeExamInfoPrice);
    
    // 驗證金額dialog
    if (!this.isPackagePriceValid) {
      const priceDialog = this.dialog.open(SingleActionDialogComponent, {
        data: {
          title: this.translateService.instant('reserve-add.price-dialog-title', {
              originalPrice: this.originalPackagePrice.toLocaleString()
          }),
          message:
            this.translateService.instant('reserve-add.price-dialog-message', {
              originalPrice: this.originalPackagePrice.toLocaleString()
            }),
          buttonText: this.translateService.instant('reserve-add.price-dialog-button'),
          showCloseButton: false,
          action: () => {
            priceDialog.close();
            const addExamItemList = this.sharedStorageService.get('addExamItemList') || [];

            // 從加選清單中移除未通過的檢項
            const updatedAddExamItemList = addExamItemList.filter((item: AddExamInfo) => !this.invalidExamItemQuestionMap?.has(item.examItem));
            this.invalidExamItemQuestionMap = undefined;
            this.sharedStorageService.set('paymentAmount', finalPaymentAmount);
            this.sharedStorageService.remove('invalidExamQuestionMap');
            this.sharedStorageService.set('addExamItemList', updatedAddExamItemList);
            this.router.navigate(['/healthcheck/reserve/additem']).then();
          },
        }
      });
      return;
    }

    // 先儲存鎖定的金額,避免後續修改 addExamItemList 導致重複計算
    this.sharedStorageService.set('paymentAmount', finalPaymentAmount);
    console.log('已儲存 paymentAmount 到 sharedStorage:', finalPaymentAmount);
    
    if (this.invalidExamItemQuestionMap) {
      // 有無法施作的檢項
      // 將方案檢項selectFlag = 0
      this.invalidExamItemQuestionMap.forEach((questions, examItem) => {
        const examItemList = this.sharedStorageService.get('examItemList');
        if (examItemList) {
          const examInfo = examItemList.find((item: ExamInfo) => item.examItem === examItem);
          if (examInfo) {
            examInfo.selectFlag = 0;
            this.sharedStorageService.set('examItemList', examItemList);
          }
        }

        const addExamItemList = this.sharedStorageService.get('addExamItemList');

        // 移除加選項目
        if (addExamItemList) {
          const addExamInfo = addExamItemList.find((item: AddExamInfo) => item.examItem === examItem);
          if (addExamInfo) {
            const index = addExamItemList.indexOf(addExamInfo);
            if (index > -1) {
              addExamItemList.splice(index, 1);
              this.sharedStorageService.set('addExamItemList', addExamItemList);
              console.log('已移除無法施作的加選檢項:', examItem);
            }
          }
        }
      });
      
      console.log('處理完無法施作檢項後,確認 sharedStorage 中的 paymentAmount:', this.sharedStorageService.get('paymentAmount'));
      // 查詢是否有要導到合適性問卷
      let request: EligibilityQuestionnaireFieldRequest = {
        orgNo: this.sharedStorageService.get('userPlanRequest')?.orgNo ?? '',
        gender: this.sharedStorageService.get('userPlanRequest')?.gender ?? '',
        isUpdate: this.sharedStorageService.get('isUpdate') ?? '0',
        examItemList: this.sharedStorageService.get('examItemList')?.map((examInfo: ExamInfo) => ({
          examItem: examInfo.examItem,
          examName: examInfo.examName,
          selectFlag: examInfo.selectFlag
        })) ?? [],
        addList: this.sharedStorageService.get('addExamItemList')?.map((addExamInfo: AddExamInfo) => ({
          examItem: addExamInfo.examItem,
          examName: addExamInfo.examName,
        })) ?? [],
      };

      const examItemList = this.sharedStorageService.get('examItemList');

      if (examItemList) {
        // 拷貝 examItemList，避免後續修改影響原始資料
        const originalExamItemList = examItemList.map(item => ({...item}));
        this.sharedStorageService.set('originalExamItemList', originalExamItemList);
      }
      // 呼叫API查詢合適性問卷欄位
      this.reserveConfirmSurveyService.getQuestionnaireFields(request).subscribe(res => {
        if (res.statusCode === 'S0000') {
          // 處理回應資料
          console.log('合適性問卷欄位:', res.data);
          if (res.data?.indicationInfos && res.data.indicationInfos.length > 0) {
            this.router.navigate(['/healthcheck/reserve/evaluation']).then();
          } else {
            // 查詢有無檢前提醒
            console.log('沒有合適性問卷，查詢有無檢前提醒');
            const examItemNameList: Array<ExamItemName> =
              this.sharedStorageService.get('examItemList')?.map((examInfo: ExamInfo) => ({
                  examItem: examInfo.examItem,
                  examName: examInfo.examName,
                  selectFlag: examInfo.selectFlag
                })) ?? [];
            const addExamItemNameList: Array<ExamItemName> =
              this.sharedStorageService
                .get('addExamItemList')
                ?.map((examItem: AddExamInfo) => ({
                  examItem: examItem.examItem,
                  examName: examItem.examName,
                })) ?? [];
          
            const request: CheckReminderRequest = {
              examItemList: examItemNameList,
              addList: addExamItemNameList,
            };
            // 查詢檢查提醒
            this.reserveConfirmReminderService
              .checkReminders(request)
              .subscribe(res => {
                if (res.data?.reminderList && res.data.reminderList.length > 0) {
                  this.reminderList = res.data?.reminderList ?? [];
        
                  this.agreeChecked = new Array(this.reminderList.length).fill(false);
                  // 導到檢前提醒頁面
                  this.router.navigate(['/healthcheck/reserve/reminder']).then();
                } 
                // 沒有合適性問卷及檢前提醒，依照前面登入狀態來決定導向頁面
                else {
                  const loginStatus = this.sharedStorageService.get('loginStatus');
                  const createMemberRequest = this.sharedStorageService.get('createMemberRequest');
                  // 預約中登入 依照前面登入狀態來決定導向頁面
                  if (loginStatus === 'memberLogin' && this.sharedStorageService.get('modifyType') === 'modifyExam') {
                    console.log('沒有合適性問卷及檢前提醒，且為memberLogin修改預約，導到選擇時間頁面');
                    this.router.navigate(['/healthcheck/reserve/timeselect']).then();
                  }
                  else if (loginStatus === 'memberLogin' && createMemberRequest?.idNo && this.authService.isLogin()) {
                    console.log('沒有合適性問卷及檢前提醒，且為memberLogin，導到填寫預約資料頁面');
                    this.router.navigate(['/healthcheck/reserve/bookinfo']).then();
                  } else if (loginStatus === 'newFriend' || loginStatus === undefined) {
                    console.log('沒有合適性問卷及檢前提醒，且為newFriend，導到登入/註冊頁面');
                    this.router
                      .navigate(['/healthcheck/login'], {
                        queryParams: { tab: LOGIN_REGISTER_TABS.REGISTER },
                      })
                      .then();
                  } else if (loginStatus === 'withHIS' || loginStatus === 'withoutHIS') {
                    console.log('沒有合適性問卷及檢前提醒，且為withHIS或withoutHIS，導到聯絡資訊頁面');
                    this.router
                      .navigate(['/healthcheck/contactinfo'], {
                        queryParams: { backToAppointment: 'true' },
                      })
                      .then();
                  }
                }
            });
          }
        }
      });
    } else {
      // 查詢是否有要導到合適性問卷
      let request: EligibilityQuestionnaireFieldRequest = {
        orgNo: this.sharedStorageService.get('userPlanRequest')?.orgNo ?? '',
        gender: this.sharedStorageService.get('userPlanRequest')?.gender ?? '',
        isUpdate: this.sharedStorageService.get('isUpdate') ?? '0',
        examItemList: this.sharedStorageService.get('examItemList')?.map((examInfo: ExamInfo) => ({
          examItem: examInfo.examItem,
          examName: examInfo.examName,
          selectFlag: examInfo.selectFlag
        })) ?? [],
        addList: this.sharedStorageService.get('addExamItemList')?.map((addExamInfo: AddExamInfo) => ({
          examItem: addExamInfo.examItem,
          examName: addExamInfo.examName,
        })) ?? [],
      };

      const examItemList = this.sharedStorageService.get('examItemList');

      if (examItemList) {
        // 拷貝 examItemList，避免後續修改影響原始資料
        const originalExamItemList = examItemList.map(item => ({...item}));
        this.sharedStorageService.set('originalExamItemList', originalExamItemList);
        
        // // 計算當前所有已選檢項的原始總價
        // const originalTotalPrice = examItemList.filter(item => (item.selectFlag ?? 1) === 1)
        //   .reduce((total, item) => {
        //     return total.add(new Decimal(item.examPrice ?? 0));
        //   }, new Decimal(0));

        // // 對每個檢項套用動態折扣率
        // examItemList.forEach(examInfo => {
        //   if ((examInfo.selectFlag ?? 1) === 1) {  
        //     const originalPrice = new Decimal(examInfo.examPrice ?? 0);          
        //     // 檢查 rateStatus，為 true 才套用折扣
        //     if (examInfo.rateStatus === true) {
        //       const discountRate = this.movedDiscountRate(originalTotalPrice);
        //       const discountedPrice = originalPrice.mul(discountRate);
        //       const roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件十進位
        //       examInfo.examPrice = roundedPrice.toString();
        //     } else {
        //       examInfo.examPrice = originalPrice.toString();
        //     }
        //   }
        //   this.sharedStorageService.set('examItemList', examItemList);
        // });
      } 

      // 呼叫API查詢合適性問卷欄位
      this.reserveConfirmSurveyService.getQuestionnaireFields(request).subscribe(res => {
        if (res.statusCode === 'S0000') {
          // 處理回應資料
          console.log('合適性問卷欄位:', res.data);
          if (res.data?.indicationInfos && res.data.indicationInfos.length > 0) {
            this.router.navigate(['/healthcheck/reserve/evaluation']).then();
          } else {
             // 查詢有無檢前提醒
             console.log('沒有合適性問卷，查詢有無檢前提醒');
            const examItemNameList: Array<ExamItemName> =
                  this.sharedStorageService.get('examItemList')?.map((examInfo: ExamInfo) => ({
                      examItem: examInfo.examItem,
                      examName: examInfo.examName,
                      selectFlag: examInfo.selectFlag
                    })) ?? [];
                const addExamItemNameList: Array<ExamItemName> =
                  this.sharedStorageService
                    .get('addExamItemList')
                    ?.map((examItem: AddExamInfo) => ({
                      examItem: examItem.examItem,
                      examName: examItem.examName,
                    })) ?? [];
            
                const request: CheckReminderRequest = {
                  examItemList: examItemNameList,
                  addList: addExamItemNameList,
                };
                // 查詢檢查提醒
                this.reserveConfirmReminderService
                  .checkReminders(request)
                  .subscribe(res => {
                    if (res.data?.reminderList && res.data.reminderList.length > 0) {
                      this.reminderList = res.data?.reminderList ?? [];
            
                      this.agreeChecked = new Array(this.reminderList.length).fill(false);
                      // 導到檢前提醒頁面
                      this.router.navigate(['/healthcheck/reserve/reminder']).then();
                    } 
                    // 沒有合適性問卷及檢前提醒，依照前面登入狀態來決定導向頁面
                    else {
                      const loginStatus = this.sharedStorageService.get('loginStatus');
                      const createMemberRequest = this.sharedStorageService.get('createMemberRequest');
                      // 預約中登入 依照前面登入狀態來決定導向頁面
                      if (loginStatus === 'memberLogin' && this.sharedStorageService.get('modifyType') === 'modifyExam') {
                        console.log('沒有合適性問卷及檢前提醒，且為memberLogin修改預約，導到選擇時間頁面');
                        this.router.navigate(['/healthcheck/reserve/timeselect']).then();
                      }
                      // 預約中登入 依照前面登入狀態來決定導向頁面
                      else if (loginStatus === 'memberLogin' && createMemberRequest?.idNo && this.authService.isLogin()) {
                        console.log('沒有合適性問卷及檢前提醒，且為memberLogin，導到填寫預約資料頁面');
                        this.router.navigate(['/healthcheck/reserve/bookinfo']).then();
                      } else if (loginStatus === 'newFriend' || loginStatus === undefined) {
                        console.log('沒有合適性問卷及檢前提醒，且為newFriend，導到登入/註冊頁面');
                        this.router
                          .navigate(['/healthcheck/login'], {
                            queryParams: { tab: LOGIN_REGISTER_TABS.REGISTER },
                          })
                          .then();
                      } else if (loginStatus === 'withHIS' || loginStatus === 'withoutHIS') {
                        console.log('沒有合適性問卷及檢前提醒，且為withHIS或withoutHIS，導到聯絡資訊頁面');
                        this.router
                          .navigate(['/healthcheck/contactinfo'], {
                            queryParams: { backToAppointment: 'true' },
                          })
                          .then();
                      }
                    }
                  });
          }
        }
      });
    }
    // 跳轉合適性問卷頁面
    // const addExamItemList = this.sharedStorageService.get('addExamItemList');
    // const examItemList = this.sharedStorageService.get('examItemList');

    // // 是否有特定檢項(胃鏡與AI瘜肉偵測)
    // const hasSpecificExam = examItemList?.some((examInfo: ExamInfo) => {
    //   return examInfo.examItem === 'LB0002000M' || examInfo.examItem === 'LB000201C';
    // }) ||
    //   addExamItemList?.some((addExamInfo: AddExamInfo) => {
    //     return addExamInfo.examItem === 'LB0002000M' || addExamInfo.examItem === 'LB000201C';
    //   });
    // if (hasSpecificExam) {
    //   this.router.navigate(['/healthcheck/reserve/evaluation']).then();
    // } else {
    //   this.router.navigate(['/healthcheck/reserve/reminder']).then();
    // }
  }

  /**
   * 計算動態折扣率
   */
  movedDiscountRate(originalPrice: Decimal): Decimal {
    console.log("計算折扣率 - originalPrice:", originalPrice.toString());
    
    // 防護：如果 originalPrice 為負數或零，返回基礎折扣率
    if (originalPrice.lte(0)) {
      console.warn("originalPrice 異常:", originalPrice.toString());
      return new Decimal(1);
    }

    const userPlanResponse = this.sharedStorageService.get('userPlanResponse');

    //取得原始售價(未折扣)
    const allcheckupPrice = new Decimal(userPlanResponse?.allCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0);
    const aiExclusivePrice = new Decimal(userPlanResponse?.aiExclusive?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0);
    const basicCheckupPrice = new Decimal(userPlanResponse?.basicCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0);

    // 取得原始折扣率
    const allCheckupRate = new Decimal(userPlanResponse?.allCheckup?.discountRate ?? 0);
    const aiExclusiveRate = new Decimal(userPlanResponse?.aiExclusive?.discountRate ?? 0);
    const basicCheckupRate = new Decimal(userPlanResponse?.basicCheckup?.discountRate ?? 0);
    
    // 當目前原價大於等於全方位方案價格時
    if (originalPrice.gte(allcheckupPrice)) {
      console.log("使用全方位折扣率");
      return allCheckupRate;
    } 
    // 當目前原價大於等於智能專屬方案價格時
    else if (originalPrice.gte(aiExclusivePrice)) {
      if (originalPrice.eq(aiExclusivePrice)) {
        console.log("使用智能專屬折扣率");
        return aiExclusiveRate;
      }
      console.log("計算全方位與智能專屬之間的折扣率");
      return allCheckupRate.sub(
        (allCheckupRate.sub(aiExclusiveRate))
          .mul((allcheckupPrice.sub(originalPrice)))
          .div((allcheckupPrice.sub(aiExclusivePrice)))
      );
    } 
    // 當目前原價大於等於基礎方案價格時
    else if (originalPrice.gte(basicCheckupPrice)) {
      if (originalPrice.eq(basicCheckupPrice)){
        console.log("使用基礎折扣率");
        return basicCheckupRate;
      }
      console.log("計算智能專屬與基礎之間的折扣率");
      return aiExclusiveRate.sub(
        ((aiExclusiveRate.sub(basicCheckupRate))).mul((aiExclusivePrice.sub(originalPrice)))
          .div((aiExclusivePrice.sub(basicCheckupPrice)))
      );
    } 
    // 當目前原價小於基礎方案價格時
    else {
      console.log("使用基礎折扣率");
      return new Decimal(basicCheckupRate);
    }
  }

  // 取得個別examType底下deptName是否展開
  getDeptOpenStateByExamType(examType: string, deptName: string) {
    return this.isDeptNameOpenByExamType.get(examType)?.get(deptName) ?? false;
  }

  // 切換個別examType底下deptName的展開狀態
  toggleDeptOpenStateByExamType(examType: string, deptName: string) {
    if (!this.isDeptNameOpenByExamType.has(examType)) {
      this.isDeptNameOpenByExamType.set(examType, new Map<string, boolean>());
    }
    const deptMap = this.isDeptNameOpenByExamType.get(examType)!;
    deptMap.set(deptName, !(deptMap.get(deptName) ?? false));
  }

  protected readonly ID_LOGIN_TYPE = ID_LOGIN_TYPE;
  
  // 檢查是否有有效的加選檢項
  get hasValidAddExamItems(): boolean {
    return this.addExamInfoList.length > 0 && 
           this.addExamInfoList.some(item => !this.invalidExamItemQuestionMap?.has(item.examItem));
  }

  // 取得企業補助金額
  get businessSponsorAmount(): number {
    const modifyType = this.sharedStorageService.get('modifyType');
    if (modifyType === 'modifyExam') {
      return Number(this.sharedStorageService.get('sponsorAmt')) ?? 0;
    } else {
      return Number(this.sharedStorageService.get('businessPlanResponse')?.companyInfo.sponsorAmt) ?? 0;
    }
  }

  ngOnDestroy() {
    // 離開頁面時清除鎖定的金額
    this.sharedStorageService.remove('lockedPaymentAmount');
  }
}
